import React, { useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { Alert, AlertDescription, AlertIcon, Box, Button, ButtonGroup, Code, Flex, HStack, Input, SimpleGrid, Text, VStack, useToast } from '@chakra-ui/react'
import { AlertTriangle, CheckCircle2, Info, RefreshCw, Upload, XCircle } from 'lucide-react'
import { control, type GatewayReport, type Issue, type LedgerReport, type SavedRun } from '@services/operators'
import { apiErrorMessage, formatDate, formatNumber, timeAgo } from '@utils/format'
import { EmptyState, Money, PageHeader, Panel, Pill, SkeletonRows, StatTile } from '@components/ui'

const SEV = {
  critical: { icon: XCircle, fg: 'red.600', bg: 'red.50', label: 'Fix now' },
  warning: { icon: AlertTriangle, fg: 'orange.600', bg: 'orange.50', label: 'Look into' },
  info: { icon: Info, fg: 'blue.600', bg: 'blue.50', label: 'For your information' },
} as const

function IssueList({ issues, clean }: { issues: Issue[]; clean: string }) {
  if (!issues.length) {
    return (
      <Flex align="center" gap={4} p={6}>
        <Flex w={12} h={12} borderRadius="full" bg="brand.50" color="brand.600" align="center" justify="center" flexShrink={0}><CheckCircle2 size={24} /></Flex>
        <Box><Text fontWeight="semibold">Everything matches</Text><Text fontSize="sm" color="gray.500">{clean}</Text></Box>
      </Flex>
    )
  }
  return (
    <VStack align="stretch" spacing={0} divider={<Box h="1px" bg="gray.100" />}>
      {issues.map((i, idx) => {
        const s = SEV[i.severity]
        const Icon = s.icon
        return (
          <Flex key={`${i.code}-${idx}`} px={5} py={4} gap={4} align="flex-start">
            <Flex w={9} h={9} borderRadius="lg" bg={s.bg} color={s.fg} align="center" justify="center" flexShrink={0}><Icon size={18} /></Flex>
            <Box flex="1" minW={0}>
              <HStack spacing={2} flexWrap="wrap">
                <Text fontWeight="semibold" color="gray.900">{i.title}</Text>
                <Pill tone={i.severity === 'critical' ? 'red' : i.severity === 'warning' ? 'orange' : 'blue'} dot={false}>{s.label}</Pill>
                {i.amount ? <Pill tone="gray" dot={false}>TZS {formatNumber(i.amount)}</Pill> : null}
              </HStack>
              <Text fontSize="sm" color="gray.600" mt={1}>{i.detail}</Text>
              {i.refs && i.refs.length > 0 && (
                <Box mt={2}><Text fontSize="xs" color="gray.500" mb={1}>Examples</Text>
                  <HStack spacing={1} flexWrap="wrap">{i.refs.slice(0, 5).map(r => <Code key={r} fontSize="xs" borderRadius="md" px={1.5}>{r}</Code>)}</HStack></Box>
              )}
            </Box>
          </Flex>
        )
      })}
    </VStack>
  )
}

function LedgerSection() {
  const qc = useQueryClient()
  const toast = useToast()
  const [days, setDays] = useState(7)
  const [shown, setShown] = useState<LedgerReport | SavedRun | null>(null)
  const runs = useQuery('recon-runs', () => control.runs().then(r => r.data))
  const latest = shown ?? runs.data?.find(r => r.kind === 'ledger') ?? null

  const run = useMutation(() => control.ledgerCheck({ from: new Date(Date.now() - days * 86400000).toISOString(), to: new Date().toISOString() }), {
    onSuccess: r => { setShown(r.data); qc.invalidateQueries('recon-runs'); qc.invalidateQueries('control-home') },
    onError: e => { toast({ title: apiErrorMessage(e), status: 'error' }) },
  })
  const summary: any = latest ? (latest as any).summary : null
  const issues: Issue[] = latest ? ((latest as any).issues ?? []) : []
  const period = latest ? ('period' in latest ? latest.period : { from: (latest as SavedRun).periodFrom, to: (latest as SavedRun).periodTo }) : null

  return (
    <>
      <Flex gap={3} mb={5} direction={{ base: 'column', md: 'row' }} justify="space-between" align={{ md: 'center' }}>
        <ButtonGroup isAttached variant="outline" size="sm">
          {[2, 7, 30].map(d => <Button key={d} onClick={() => setDays(d)} bg={days === d ? 'gray.900' : 'white'} color={days === d ? 'white' : 'gray.600'} _hover={{ bg: days === d ? 'gray.800' : 'gray.50' }}>Last {d} days</Button>)}
        </ButtonGroup>
        <Button leftIcon={<RefreshCw size={14} />} isLoading={run.isLoading} onClick={() => run.mutate()}>Run money check</Button>
      </Flex>

      {runs.isLoading ? <SkeletonRows rows={3} /> : !latest ? (
        <Panel><EmptyState icon={<CheckCircle2 size={26} />} title="No check has run yet" body="It runs automatically every day. You can also run it now." /></Panel>
      ) : (
        <>
          <Text fontSize="sm" color="gray.500" mb={3}>
            Rides delivered {period && `${formatDate(period.from)} – ${formatDate(period.to)}`} · checked {timeAgo('createdAt' in latest ? latest.createdAt : new Date())}
          </Text>
          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mb={6}>
            <StatTile label="Rides delivered" value={formatNumber(summary.ridesDelivered)} hint={<Money value={summary.fareDelivered} size="sm" />} icon={<CheckCircle2 size={20} />} />
            <StatTile label="Settled to companies" value={formatNumber(summary.ridesSettled)} hint={<>Net <Money value={summary.netToOperators} size="sm" /></>} tone="blue" />
            <StatTile label="Our commission" value={<Money value={summary.commission} size="md" />} hint={summary.levyDeducted ? <>Levy passed on <Money value={summary.levyDeducted} size="sm" /></> : undefined} tone="purple" />
            <StatTile label="Not settled" value={formatNumber(summary.ridesUnsettled)} hint={<Money value={summary.fareUnsettled} size="sm" />} tone={summary.ridesUnsettled ? 'red' : 'gray'} />
            <StatTile label="Discounts we funded" value={<Money value={summary.promoAbsorbed} size="md" />} tone="orange" />
            <StatTile label="Refunds taken back" value={<Money value={summary.refundsPosted} size="md" />} tone="gray" />
            <StatTile label="Paid out" value={<Money value={summary.paidOut} size="md" />} tone="gray" />
            <StatTile label="Companies owe us" value={<Money value={summary.owedByCompanies} size="md" />} tone={summary.owedByCompanies ? 'orange' : 'gray'} />
          </SimpleGrid>
          <Panel title="What the check found" flush>
            <IssueList issues={issues} clean="Every paid and delivered ride is settled, every credit has a payment behind it, and every withdrawal adds up." />
          </Panel>
        </>
      )}

      {(runs.data?.length ?? 0) > 1 && (
        <Panel title="Earlier checks" flush mt={6}>
          <VStack align="stretch" spacing={0} divider={<Box h="1px" bg="gray.100" />}>
            {runs.data!.slice(0, 8).map(r => (
              <Flex key={r.id} as="button" type="button" px={5} py={3} justify="space-between" align="center" _hover={{ bg: 'gray.50' }} onClick={() => setShown(r)} textAlign="left">
                <Text fontSize="sm">{r.kind === 'ledger' ? 'Money check' : 'ZenoPay statement'} · {formatDate(r.createdAt, true)}</Text>
                <Pill tone={r.status === 'clean' ? 'brand' : 'orange'}>{r.status === 'clean' ? 'Matched' : `${r.issues.filter(i => i.severity !== 'info').length} to look at`}</Pill>
              </Flex>
            ))}
          </VStack>
        </Panel>
      )}
    </>
  )
}

function GatewaySection() {
  const qc = useQueryClient()
  const toast = useToast()
  const input = useRef<HTMLInputElement>(null)
  const [csv, setCsv] = useState('')
  const [name, setName] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [needPeriod, setNeedPeriod] = useState(false)
  const [report, setReport] = useState<GatewayReport | null>(null)

  const pick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    if (!f) return
    if (f.size > 3_500_000) { toast({ title: 'That file is too large. Upload one month at a time.', status: 'error' }); return }
    setCsv(await f.text()); setName(f.name); setReport(null); setNeedPeriod(false)
  }
  const check = useMutation(() => control.gatewayCheck({ csv, from: from || undefined, to: to ? new Date(new Date(to).getTime() + 86400000).toISOString() : undefined }), {
    onSuccess: r => { setReport(r.data); setNeedPeriod(false); qc.invalidateQueries('recon-runs') },
    onError: (e: any) => {
      if (e?.response?.data?.error?.code === 'PERIOD_REQUIRED') setNeedPeriod(true)
      else toast({ title: apiErrorMessage(e), status: 'error', duration: 7000 })
    },
  })

  return (
    <Panel title="Match a ZenoPay statement" subtitle="Find payments that exist on one side but not the other" mt={8}>
      <Text fontSize="sm" color="gray.600" mb={4} maxW="2xl">
        Download your transactions as CSV from the ZenoPay dashboard and upload it here. We match each line to our payments by order id, reference or transaction id, and compare the amounts. The file needs a reference column (<Code>order_id</Code>, <Code>reference</Code> or <Code>transid</Code>) and an <Code>amount</Code> column.
      </Text>
      <Flex gap={3} align="center" flexWrap="wrap">
        <input ref={input} type="file" accept=".csv,text/csv,text/plain" hidden onChange={pick} />
        <Button variant="outline" leftIcon={<Upload size={14} />} onClick={() => input.current?.click()}>{name ? 'Choose another file' : 'Choose CSV file'}</Button>
        {name && <Text fontSize="sm" color="gray.600">{name}</Text>}
        <Button isDisabled={!csv} isLoading={check.isLoading} onClick={() => check.mutate()}>Check statement</Button>
      </Flex>
      {needPeriod && (
        <Box mt={4}>
          <Alert status="info" borderRadius="xl" fontSize="sm" mb={3}><AlertIcon /><AlertDescription>This file has no dates, so tell us which days it covers.</AlertDescription></Alert>
          <HStack><Input type="date" size="sm" w="auto" value={from} onChange={e => setFrom(e.target.value)} aria-label="From" /><Text>to</Text><Input type="date" size="sm" w="auto" value={to} onChange={e => setTo(e.target.value)} aria-label="To" />
            <Button size="sm" isDisabled={!from || !to} isLoading={check.isLoading} onClick={() => check.mutate()}>Check</Button></HStack>
        </Box>
      )}
      {report && (
        <Box mt={6}>
          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mb={5}>
            <StatTile label="Lines in statement" value={formatNumber(report.summary.statementRows)} hint={<Money value={report.summary.statementTotal} size="sm" />} />
            <StatTile label="Matched" value={formatNumber(report.summary.matched)} hint={<Money value={report.summary.matchedTotal} size="sm" />} />
            <StatTile label="Not in our records" value={formatNumber(report.summary.missingFromOurs)} tone={report.summary.missingFromOurs ? 'red' : 'gray'} />
            <StatTile label="Not in the statement" value={formatNumber(report.summary.missingFromStatement)} hint={<Money value={report.summary.missingTotal} size="sm" />} tone={report.summary.missingFromStatement ? 'orange' : 'gray'} />
          </SimpleGrid>
          <Box border="1px solid" borderColor="gray.100" borderRadius="xl" overflow="hidden">
            <IssueList issues={report.issues} clean="Every line in the statement matches a payment we recorded, with the same amount." />
          </Box>
        </Box>
      )}
    </Panel>
  )
}

export default function ReconciliationPage() {
  return (
    <>
      <PageHeader eyebrow="Money" title="Money check" subtitle="Confirm the books match the money we collected, before you pay anyone out." />
      <LedgerSection />
      <GatewaySection />
    </>
  )
}
